import { inferLegendIconSourceFromSelectorMapKey } from '@/pages/dashboard/components/EventMetricTrend/legendDimensionIcons';
import type { SeriesConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import { useLocal, useSelectorMap } from '@/pages/dashboard/utils/customHook';
import React, { useMemo } from 'react';
import {
  useKpiEventMetricMergedData,
} from '../KpiEventMetricTrendService/_hooks';
import {
  buildAnomalyScatterSeries,
  filterAnomalyByMetricKey,
  shouldApplyAnomalyMerge,
} from '../KpiEventMetricTrendService/_utils';
import { pickKpiOverviewAnomalyLayer } from '../AnomalyService';
import {
  buildDimensionTrendDownloadColumns,
} from './_utils';
import type {
  IEventMetricTrendChildStubProps,
  IKpiEventDimensionTrendInnerProps,
  TTrendBodyWithDimension,
  TTrendServiceBodyEnriched,
  TTrendServiceDataWithSeries,
  TDimensionLegendCompositeKeyPair,
} from './_type';
import { mapCompositeDimensionLegend } from './_mapCompositeDimensionLegend';

/**
 * 维拆接口未返回前 dimensionSeriesConfig 为空；pageConfig 常不写静态 seriesConfig。
 * 此时 useEventMetricTrendTable 得到 null，EventMetricTrend 会走到 MetricsCardWrap 分支，
 * 无 CardBox 的固定 height，加载态整块卡片会变矮。用占位线与真实 dim_v_* 对齐即可恢复高度。
 */
const buildDimensionTrendPlaceholderSeries = (
  valueFormatRule: SeriesConfig['formatRule'] | undefined,
): SeriesConfig[] => {
  const fmt =
    valueFormatRule ?? ('thousands2Decimal' as SeriesConfig['formatRule']);
  return [
    {
      metricKey: 'dim_v_0',
      cnName: '-',
      enName: '-',
      dateKey: 'date',
      formatRule: fmt,
      color: '#69D19C',
    },
    {
      metricKey: 'dim_v_1',
      cnName: '-',
      enName: '-',
      dateKey: 'date',
      formatRule: fmt,
      color: '#5086F3',
    },
  ];
};

const KpiEventDimensionTrendInner = (props: IKpiEventDimensionTrendInnerProps) => {
  const {
    children,
    eventData: rawEventList,
    loading: eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    mergeAnomalyIntoTrend,
    anomalyMetricKey,
    kpiOverviewDataScope = 'all',
    data: serviceData,
    selectorMapKey,
    dimensionLegendCompositeKeys,
    forceNoData,
    downloadIncludeLastWeek,
    ...rest
  } = props;

  const trendState = serviceData?.trendData;

  const { isEN } = useLocal();

  const { getDimensionLegendEnCn } = useSelectorMap();

  const mergedData = useKpiEventMetricMergedData({
    serviceData,
    trendState,
    rawEventList,
    eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    mergeAnomalyIntoTrend,
    anomalyMetricKey,
    kpiOverviewDataScope,
  });

  const chartBody = mergedData?.trendData?.data as
    | TTrendBodyWithDimension
    | undefined;
  const dimensionSeriesConfig = chartBody?.dimensionSeriesConfig;

  const mapKey =
    typeof selectorMapKey === 'string' && selectorMapKey.trim() !== ''
      ? selectorMapKey.trim()
      : 'ua_network';

  const dimensionSeriesConfigMapped = useMemo(() => {
    if (!Array.isArray(dimensionSeriesConfig) || dimensionSeriesConfig.length === 0) {
      return dimensionSeriesConfig;
    }
    const composite =
      Array.isArray(dimensionLegendCompositeKeys) &&
      dimensionLegendCompositeKeys.length === 2
        ? (dimensionLegendCompositeKeys as TDimensionLegendCompositeKeyPair)
        : undefined;
    return dimensionSeriesConfig.map((s: SeriesConfig) => {
      const raw =
        typeof s.dimRawCode === 'string' && s.dimRawCode !== ''
          ? s.dimRawCode
          : s.cnName;
      const { enName, cnName } = mapCompositeDimensionLegend(
        raw,
        mapKey,
        composite,
        getDimensionLegendEnCn,
      );
      return { ...s, enName, cnName };
    });
  }, [
    dimensionSeriesConfig,
    dimensionLegendCompositeKeys,
    getDimensionLegendEnCn,
    mapKey,
  ]);

  const dimensionSeriesPlaceholder = useMemo(
    () =>
      buildDimensionTrendPlaceholderSeries(
        chartBody?.dimensionValueFormatRule as SeriesConfig['formatRule'] | undefined,
      ),
    [chartBody?.dimensionValueFormatRule],
  );

  const downloadColumnsForDimension = useMemo(() => {
    if (!chartBody || chartBody.dimensionSeriesConfig === undefined) {
      return null;
    }
    const cfg =
      dimensionSeriesConfigMapped && dimensionSeriesConfigMapped.length > 0
        ? dimensionSeriesConfigMapped
        : chartBody.dimensionSeriesConfig;
    return buildDimensionTrendDownloadColumns(cfg, {
      isEN,
      valueFormatRule: chartBody.dimensionValueFormatRule,
      includeLastWeekColumns: downloadIncludeLastWeek !== false,
    });
  }, [
    chartBody,
    dimensionSeriesConfigMapped,
    isEN,
    downloadIncludeLastWeek,
  ]);

  const dimAnomalyScatterConfigs = useMemo(() => {
    const dimCfg = dimensionSeriesConfigMapped ?? dimensionSeriesConfig;
    if (!dimCfg?.length || !anomalyResult || !anomalyMetricKey) {
      return [];
    }
    const filteredAnomaly = filterAnomalyByMetricKey(
      pickKpiOverviewAnomalyLayer(anomalyResult, kpiOverviewDataScope),
      anomalyMetricKey,
    );
    if (!shouldApplyAnomalyMerge(filteredAnomaly, anomalyResultError)) {
      return [];
    }
    const sd = (mergedData?.trendData?.data as TTrendServiceDataWithSeries | undefined)
      ?.seriesData as Record<string, unknown>[] | undefined;
    if (!Array.isArray(sd) || sd.length === 0) {
      return [];
    }
    return buildAnomalyScatterSeries(
      filteredAnomaly,
      anomalyMetricKey,
      sd,
      dimCfg,
    ).anomalySeriesConfigs;
  }, [
    dimensionSeriesConfigMapped,
    dimensionSeriesConfig,
    anomalyResult,
    anomalyResultError,
    anomalyMetricKey,
    kpiOverviewDataScope,
    mergedData,
  ]);

  const enrichedMergedData = useMemo(() => {
    const origData = mergedData?.trendData?.data as
      | TTrendServiceBodyEnriched
      | undefined;
    if (!downloadColumnsForDimension || !origData?.seriesData) {
      return mergedData;
    }
    const sd = origData.seriesData;
    if (!Array.isArray(sd) || sd.length === 0) {
      return mergedData;
    }
    const enrichedSeriesData = [
      { ...sd[0], _dlCols: downloadColumnsForDimension },
      ...sd.slice(1),
    ];
    return {
      ...mergedData,
      trendData: {
        ...mergedData.trendData,
        data: {
          ...origData,
          seriesData: enrichedSeriesData,
        },
      },
    };
  }, [mergedData, downloadColumnsForDimension]);

  return (
    <>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        const useDyn =
          Array.isArray(dimensionSeriesConfigMapped) &&
          dimensionSeriesConfigMapped.length > 0;
        const childProps = child.props as IEventMetricTrendChildStubProps;
        const baseChart = childProps.chartConfig ?? {};
        const compositeForLegend =
          Array.isArray(dimensionLegendCompositeKeys) &&
          dimensionLegendCompositeKeys.length === 2
            ? (dimensionLegendCompositeKeys as TDimensionLegendCompositeKeyPair)
            : undefined;
        const pageStaticSeries = (childProps.seriesConfig ?? []) as SeriesConfig[];
        let baseSeries: SeriesConfig[];
        if (useDyn) {
          baseSeries = dimensionSeriesConfigMapped as SeriesConfig[];
        } else if (pageStaticSeries.length > 0) {
          baseSeries = pageStaticSeries;
        } else {
          baseSeries = dimensionSeriesPlaceholder;
        }
        const finalSeriesConfig = dimAnomalyScatterConfigs.length
          ? [...baseSeries, ...dimAnomalyScatterConfigs]
          : baseSeries;
        const injectedProps: React.Attributes & Record<string, unknown> = {
          ...rest,
          data: enrichedMergedData,
          forceNoData,
          chartConfig: {
            ...baseChart,
            showSymbol: baseChart.showSymbol ?? false,
            legendIconSource:
              baseChart.legendIconSource ??
              inferLegendIconSourceFromSelectorMapKey(mapKey),
            legendIconCompositeKeys:
              baseChart.legendIconCompositeKeys ?? compositeForLegend,
          },
          seriesConfig: finalSeriesConfig,
        };
        return React.cloneElement(child, injectedProps);
      })}
    </>
  );
};

export default KpiEventDimensionTrendInner;
